import {
  AnalyticsAlertStatusType,
  GetAllActiveHubAlertsQuery,
  GetAllHiddenHubAlertsQuery,
  UpdateAnalyticsAlertsStatusesMutation,
  UpdateAnalyticsAlertsStatusesMutationVariables,
} from '@/types';
import {
  filterOutAlertsByUUID,
  parseCardDataJson,
  stringifyCardData,
} from '@/utils/update-alert-cache';
import { GET_ALL_ACTIVE_HUB_ALERTS_QUERY } from './use-insights-hub-data';
import { GET_ALL_HIDDEN_HUB_ALERTS_QUERY } from './use-hidden-alerts-data';
import { gql, useMutation } from '@apollo/client';
import { HubCardData } from './types';
import { partnerSupplier } from '@/utils/environment';

export const UPDATE_ANALYTICS_ALERTS_STATUSES_MUTATION = gql`
  mutation UpdateAnalyticsAlertsStatuses(
    $alertStatus: AnalyticsAlertStatusType!
    $alertUUIDs: [uuid!]!
    $businessOrganizationUUID: uuid!
  ) {
    updateAnalyticsAlertsStatuses(
      alert_status: $alertStatus
      alert_ids: $alertUUIDs
      business_organization_id: $businessOrganizationUUID
    ) {
      updatedCount: updated_count
    }
  }
`;

export const useAnalyticsAlertActions = (supplierUUID: string) => {
  const [updateAlerts] = useMutation<
    UpdateAnalyticsAlertsStatusesMutation,
    UpdateAnalyticsAlertsStatusesMutationVariables
  >(UPDATE_ANALYTICS_ALERTS_STATUSES_MUTATION);

  // CASE 1: Hide alert
  //   - remove alert from active alerts
  //   - add alert to hidden alerts
  const hideActiveAlerts = async (alertUUIDsToHide: string[]) => {
    return updateAlerts({
      optimisticResponse: {
        updateAnalyticsAlertsStatuses: {
          updatedCount: alertUUIDsToHide.length,
        },
      },
      update: (client, { data, errors }) => {
        const updatedCount = data?.updateAnalyticsAlertsStatuses.updatedCount;
        // Known issues: Since we provide a successful optimistic response and no rollback logic
        //   - if the BE returns an error, the cache will remain fully updated
        //   - if the BE returns a partial update, the cache will remain fully updated
        //   - this means we may have hidden alerts that are still active on the BE
        if (!errors && updatedCount === alertUUIDsToHide.length) {
          let foundHiddenAlerts: HubCardData[] = [];
          const activeData = client.readQuery<GetAllActiveHubAlertsQuery>({
            query: GET_ALL_ACTIVE_HUB_ALERTS_QUERY,
            variables: {
              businessOrganizationUUID:
                partnerSupplier.businessOrganizationUUID,
              supplierUUID,
            },
          });

          // if no active alerts in the cache, we should return
          // this is unlikely to happen, but we should handle it for type safety
          if (!activeData) {
            return;
          }

          const currentActiveAlerts = parseCardDataJson(
            activeData.getAllHubAlerts.card_data_json,
          );
          const newActiveAlerts = filterOutAlertsByUUID(
            currentActiveAlerts,
            alertUUIDsToHide,
          );
          client.writeQuery<GetAllActiveHubAlertsQuery>({
            data: {
              getAllHubAlerts: {
                __typename: 'GetAllHubAnalyticsAlertsResponse',
                card_data_json: stringifyCardData(newActiveAlerts),
              },
            },
            query: GET_ALL_ACTIVE_HUB_ALERTS_QUERY,
            variables: {
              businessOrganizationUUID:
                partnerSupplier.businessOrganizationUUID,
              supplierUUID,
            },
          });
          // identify active alerts that are now hidden to add them to the hidden alerts list
          foundHiddenAlerts = currentActiveAlerts.filter((alert) =>
            alertUUIDsToHide.includes(alert.alert_uuid),
          );
          // add removed alerts to hidden alerts
          const hiddenData = client.readQuery<GetAllHiddenHubAlertsQuery>({
            query: GET_ALL_HIDDEN_HUB_ALERTS_QUERY,
            variables: {
              businessOrganizationUUID:
                partnerSupplier.businessOrganizationUUID,
              supplierUUID,
            },
          });

          if (!hiddenData) {
            return;
          }

          const currentHiddenAlerts = parseCardDataJson(
            hiddenData.getAllHubAlerts.card_data_json,
          );

          // mark all foundHiddenAlerts with `alert_status: AnalyticsAlertStatusType.Hidden`
          const markedAsHiddenAlerts = foundHiddenAlerts.map((alert) => ({
            ...alert,
            alert_status: AnalyticsAlertStatusType.Hidden,
          }));

          const newHiddenAlerts = [
            ...currentHiddenAlerts,
            ...markedAsHiddenAlerts,
          ];

          client.writeQuery<GetAllHiddenHubAlertsQuery>({
            data: {
              getAllHubAlerts: {
                __typename: 'GetAllHubAnalyticsAlertsResponse',
                card_data_json: stringifyCardData(newHiddenAlerts),
              },
            },
            query: GET_ALL_HIDDEN_HUB_ALERTS_QUERY,
            variables: {
              businessOrganizationUUID:
                partnerSupplier.businessOrganizationUUID,
              supplierUUID,
            },
          });
        }
      },
      variables: {
        alertStatus: AnalyticsAlertStatusType.Hidden,
        alertUUIDs: alertUUIDsToHide,
        businessOrganizationUUID: partnerSupplier.businessOrganizationUUID,
      },
    });
  };

  // CASE 2: Delete alert
  //   note: we can blindly remove the alert from both caches
  //   - attempt to remove alert from active alerts
  //   - attempt to remove alert from hidden alerts
  const deleteActiveAlerts = async (alertUUIDsToDelete: string[]) => {
    return updateAlerts({
      optimisticResponse: {
        updateAnalyticsAlertsStatuses: {
          updatedCount: alertUUIDsToDelete.length,
        },
      },
      update: (client, { data, errors }) => {
        const updatedCount = data?.updateAnalyticsAlertsStatuses.updatedCount;
        // Known issues: Since we provide a successful optimistic response and no rollback logic
        //   - if the BE returns an error, the cache will remain fully updated
        //   - if the BE returns a partial update, the cache will remain fully updated
        //   - this means we may have deleted alerts that are still active on the BE
        if (!errors && updatedCount === alertUUIDsToDelete.length) {
          const activeData = client.readQuery<GetAllActiveHubAlertsQuery>({
            query: GET_ALL_ACTIVE_HUB_ALERTS_QUERY,
            variables: {
              businessOrganizationUUID:
                partnerSupplier.businessOrganizationUUID,
              supplierUUID,
            },
          });

          // if no active alerts in the cache, we should return
          // this is unlikely to happen, but we should handle it for type safety
          if (!activeData) {
            return;
          }

          const currentAlerts = parseCardDataJson(
            activeData.getAllHubAlerts.card_data_json,
          );
          const remainingAlerts = filterOutAlertsByUUID(
            currentAlerts,
            alertUUIDsToDelete,
          );

          client.writeQuery<GetAllActiveHubAlertsQuery>({
            data: {
              getAllHubAlerts: {
                __typename: 'GetAllHubAnalyticsAlertsResponse',
                card_data_json: stringifyCardData(remainingAlerts),
              },
            },
            query: GET_ALL_ACTIVE_HUB_ALERTS_QUERY,
            variables: {
              businessOrganizationUUID:
                partnerSupplier.businessOrganizationUUID,
              supplierUUID,
            },
          });
        }
      },
      variables: {
        alertStatus: AnalyticsAlertStatusType.Delete,
        alertUUIDs: alertUUIDsToDelete,
        businessOrganizationUUID: partnerSupplier.businessOrganizationUUID,
      },
    });
  };

  const deleteHiddenAlerts = async (alertUUIDsToDelete: string[]) => {
    return updateAlerts({
      optimisticResponse: {
        updateAnalyticsAlertsStatuses: {
          updatedCount: alertUUIDsToDelete.length,
        },
      },
      update: (client, { data, errors }) => {
        const updatedCount = data?.updateAnalyticsAlertsStatuses.updatedCount;
        // Known issues: Since we provide a successful optimistic response and no rollback logic
        //   - if the BE returns an error, the cache will remain fully updated
        //   - if the BE returns a partial update, the cache will remain fully updated
        //   - this means we may have deleted alerts that are still hidden on the BE
        if (!errors && updatedCount === alertUUIDsToDelete.length) {
          const hiddenData = client.readQuery<GetAllHiddenHubAlertsQuery>({
            query: GET_ALL_HIDDEN_HUB_ALERTS_QUERY,
            variables: {
              businessOrganizationUUID:
                partnerSupplier.businessOrganizationUUID,
              supplierUUID,
            },
          });

          if (!hiddenData) {
            return;
          }

          const currentHiddenAlerts = parseCardDataJson(
            hiddenData.getAllHubAlerts.card_data_json,
          );
          const remainingHiddenAlerts = filterOutAlertsByUUID(
            currentHiddenAlerts,
            alertUUIDsToDelete,
          );

          client.writeQuery<GetAllHiddenHubAlertsQuery>({
            data: {
              getAllHubAlerts: {
                __typename: 'GetAllHubAnalyticsAlertsResponse',
                card_data_json: stringifyCardData(remainingHiddenAlerts),
              },
            },
            query: GET_ALL_HIDDEN_HUB_ALERTS_QUERY,
            variables: {
              businessOrganizationUUID:
                partnerSupplier.businessOrganizationUUID,
              supplierUUID,
            },
          });
        }
      },
      variables: {
        alertStatus: AnalyticsAlertStatusType.Delete,
        alertUUIDs: alertUUIDsToDelete,
        businessOrganizationUUID: partnerSupplier.businessOrganizationUUID,
      },
    });
  };

  // CASE 3: Unhide alert
  //   - remove alert from hidden alerts
  //   - add alert to active alerts
  const unhideActiveAlerts = async (
    alertsToUnhide: HubCardData[],
    index: number,
  ) => {
    const alertUUIDsToUnhide = alertsToUnhide.map((alert) => alert.alert_uuid);

    return updateAlerts({
      optimisticResponse: {
        updateAnalyticsAlertsStatuses: {
          updatedCount: alertUUIDsToUnhide.length,
        },
      },
      update: (client, { data, errors }) => {
        const updatedCount = data?.updateAnalyticsAlertsStatuses.updatedCount;
        // Known issues: Since we provide a successful optimistic response and no rollback logic
        //   - if the BE returns an error, the cache will remain fully updated
        //   - if the BE returns a partial update, the cache will remain fully updated
        //   - this means we may have unhidden alerts that are still hidden on the BE
        if (!errors && updatedCount === alertUUIDsToUnhide.length) {
          const activeData = client.readQuery<GetAllActiveHubAlertsQuery>({
            query: GET_ALL_ACTIVE_HUB_ALERTS_QUERY,
            variables: {
              businessOrganizationUUID:
                partnerSupplier.businessOrganizationUUID,
              supplierUUID,
            },
          });

          if (!activeData) {
            return;
          }

          const activeAlerts = parseCardDataJson(
            activeData.getAllHubAlerts.card_data_json,
          );
          activeAlerts.splice(index, 0, ...alertsToUnhide);

          client.writeQuery<GetAllActiveHubAlertsQuery>({
            data: {
              getAllHubAlerts: {
                __typename: 'GetAllHubAnalyticsAlertsResponse',
                card_data_json: stringifyCardData(activeAlerts),
              },
            },
            query: GET_ALL_ACTIVE_HUB_ALERTS_QUERY,
            variables: {
              businessOrganizationUUID:
                partnerSupplier.businessOrganizationUUID,
              supplierUUID,
            },
          });

          const hiddenData = client.readQuery<GetAllHiddenHubAlertsQuery>({
            query: GET_ALL_HIDDEN_HUB_ALERTS_QUERY,
            variables: {
              businessOrganizationUUID:
                partnerSupplier.businessOrganizationUUID,
              supplierUUID,
            },
          });

          if (!hiddenData) {
            return;
          }

          const currentHiddenAlerts = parseCardDataJson(
            hiddenData.getAllHubAlerts.card_data_json,
          );
          const newHiddenAlerts = filterOutAlertsByUUID(
            currentHiddenAlerts,
            alertUUIDsToUnhide,
          );

          client.writeQuery<GetAllHiddenHubAlertsQuery>({
            data: {
              getAllHubAlerts: {
                __typename: 'GetAllHubAnalyticsAlertsResponse',
                card_data_json: stringifyCardData(newHiddenAlerts),
              },
            },
            query: GET_ALL_HIDDEN_HUB_ALERTS_QUERY,
            variables: {
              businessOrganizationUUID:
                partnerSupplier.businessOrganizationUUID,
              supplierUUID,
            },
          });
        }
      },
      variables: {
        alertStatus: AnalyticsAlertStatusType.Active,
        alertUUIDs: alertUUIDsToUnhide,
        businessOrganizationUUID: partnerSupplier.businessOrganizationUUID,
      },
    });
  };

  // CASE 4: Undelete alert
  //   - if alert.status === 'active'
  //     - add alert to active alerts
  //   - if alert.status === 'hidden'
  //     - add alert to hidden alerts
  const undeleteActiveAlerts = async (
    alertsToUndelete: HubCardData[],
    index: number,
  ) => {
    const alertUUIDsToUndelete = alertsToUndelete.map(
      (alert) => alert.alert_uuid,
    );

    return updateAlerts({
      optimisticResponse: {
        updateAnalyticsAlertsStatuses: {
          updatedCount: alertUUIDsToUndelete.length,
        },
      },
      update: (client, { data, errors }) => {
        const updatedCount = data?.updateAnalyticsAlertsStatuses.updatedCount;
        // Known issues: Since we provide a successful optimistic response and no rollback logic
        //   - if the BE returns an error, the cache will remain fully updated
        //   - if the BE returns a partial update, the cache will remain fully updated
        //   - this means we may have unhidden alerts that are still hidden on the BE
        if (!errors && updatedCount === alertUUIDsToUndelete.length) {
          const activeData = client.readQuery<GetAllActiveHubAlertsQuery>({
            query: GET_ALL_ACTIVE_HUB_ALERTS_QUERY,
            variables: {
              businessOrganizationUUID:
                partnerSupplier.businessOrganizationUUID,
              supplierUUID,
            },
          });

          if (!activeData) {
            return;
          }

          const activeAlerts = parseCardDataJson(
            activeData.getAllHubAlerts.card_data_json,
          );
          activeAlerts.splice(index, 0, ...alertsToUndelete);

          client.writeQuery<GetAllActiveHubAlertsQuery>({
            data: {
              getAllHubAlerts: {
                __typename: 'GetAllHubAnalyticsAlertsResponse',
                card_data_json: stringifyCardData(activeAlerts),
              },
            },
            query: GET_ALL_ACTIVE_HUB_ALERTS_QUERY,
            variables: {
              businessOrganizationUUID:
                partnerSupplier.businessOrganizationUUID,
              supplierUUID,
            },
          });
        }
      },
      variables: {
        alertStatus: AnalyticsAlertStatusType.Active,
        alertUUIDs: alertUUIDsToUndelete,
        businessOrganizationUUID: partnerSupplier.businessOrganizationUUID,
      },
    });
  };

  const undeleteHiddenAlerts = async (
    alertsToUndelete: HubCardData[],
    index: number,
  ) => {
    const alertUUIDsToUndelete = alertsToUndelete.map(
      (alert) => alert.alert_uuid,
    );

    return updateAlerts({
      optimisticResponse: {
        updateAnalyticsAlertsStatuses: {
          updatedCount: alertUUIDsToUndelete.length,
        },
      },
      update: (client, { data, errors }) => {
        const updatedCount = data?.updateAnalyticsAlertsStatuses.updatedCount;
        if (!errors && updatedCount === alertUUIDsToUndelete.length) {
          const hiddenData = client.readQuery<GetAllHiddenHubAlertsQuery>({
            query: GET_ALL_HIDDEN_HUB_ALERTS_QUERY,
            variables: {
              businessOrganizationUUID:
                partnerSupplier.businessOrganizationUUID,
              supplierUUID,
            },
          });

          if (!hiddenData) {
            return;
          }

          const hiddenAlerts = parseCardDataJson(
            hiddenData.getAllHubAlerts.card_data_json,
          );
          hiddenAlerts.splice(index, 0, ...alertsToUndelete);

          client.writeQuery<GetAllHiddenHubAlertsQuery>({
            data: {
              getAllHubAlerts: {
                __typename: 'GetAllHubAnalyticsAlertsResponse',
                card_data_json: stringifyCardData(hiddenAlerts),
              },
            },
            query: GET_ALL_HIDDEN_HUB_ALERTS_QUERY,
            variables: {
              businessOrganizationUUID:
                partnerSupplier.businessOrganizationUUID,
              supplierUUID,
            },
          });
        }
      },
      variables: {
        alertStatus: AnalyticsAlertStatusType.Hidden,
        alertUUIDs: alertUUIDsToUndelete,
        businessOrganizationUUID: partnerSupplier.businessOrganizationUUID,
      },
    });
  };

  return {
    deleteActiveAlerts,
    deleteHiddenAlerts,
    hideActiveAlerts,
    undeleteActiveAlerts,
    undeleteHiddenAlerts,
    unhideActiveAlerts,
  };
};
